import { api } from "@app/api";
import { createSelector } from "@app/fx";
import { defaultEntity, defaultHalHref, extractIdFromLink } from "@app/hal";
import { type WebState, schema } from "@app/schema";
import type {
  DeployAppExternalAwsRdsConnection,
  HalEmbedded,
  LinkResponse,
} from "@app/types";

// Response payload from Deploy API for app external AWS RDS connections
export interface DeployAppExternalAwsRdsConnectionResponse {
  id: number;
  database_name: string;
  database_user: string;
  superuser: boolean;
  created_at: string;
  updated_at: string;
  _links: {
    self: LinkResponse;
    app: LinkResponse;
    external_aws_resource: LinkResponse;
    external_aws_account: LinkResponse;
  };
  _type: "app_external_aws_rds_connection";
}

export const defaultAppExternalAwsRdsConnectionResponse = (
  c: Partial<DeployAppExternalAwsRdsConnectionResponse> = {},
): DeployAppExternalAwsRdsConnectionResponse => {
  const now = new Date().toISOString();
  return {
    id: 0,
    database_name: "",
    database_user: "",
    superuser: false,
    created_at: now,
    updated_at: now,
    _links: {
      self: defaultHalHref(),
      app: defaultHalHref(),
      external_aws_resource: defaultHalHref(),
      external_aws_account: defaultHalHref(),
      ...(c._links || {}),
    },
    _type: "app_external_aws_rds_connection",
    ...c,
  };
};

export const deserializeAppExternalAwsRdsConnection = (
  payload: DeployAppExternalAwsRdsConnectionResponse,
): DeployAppExternalAwsRdsConnection => {
  return {
    id: `${payload.id}`,
    databaseName: payload.database_name,
    databaseUser: payload.database_user,
    superuser: payload.superuser,
    appId: extractIdFromLink(payload._links.app),
    externalAwsResourceId: extractIdFromLink(
      payload._links.external_aws_resource,
    ),
    externalAwsAccountId: extractIdFromLink(
      payload._links.external_aws_account,
    ),
    createdAt: payload.created_at,
    updatedAt: payload.updated_at,
  };
};

// API endpoints
export const fetchAppExternalAwsRdsConnectionsByAppId = api.get<
  { appId: string },
  HalEmbedded<{
    app_external_aws_rds_connections: DeployAppExternalAwsRdsConnectionResponse[];
  }>
>("/apps/:appId/app_external_aws_rds_connections", function* (ctx, next) {
  yield* next();
  if (!ctx.json.ok) {
    return;
  }

  const { value } = ctx.json;
  const connectionsMap =
    value._embedded.app_external_aws_rds_connections.reduce<
      Record<string, DeployAppExternalAwsRdsConnection>
    >((acc, connection) => {
      const deserialized = deserializeAppExternalAwsRdsConnection(connection);
      acc[deserialized.id] = deserialized;
      return acc;
    }, {});

  yield* schema.update(schema.appExternalAwsRdsConnections.add(connectionsMap));
});

export const fetchAppExternalAwsRdsConnectionsByExternalAwsResourceId = api.get<
  { resourceId: string },
  HalEmbedded<{
    app_external_aws_rds_connections: DeployAppExternalAwsRdsConnectionResponse[];
  }>
>(
  "/external_aws_resources/:resourceId/app_external_aws_rds_connections",
  function* (ctx, next) {
    yield* next();
    if (!ctx.json.ok) {
      return;
    }

    const { value } = ctx.json;
    const connectionsMap =
      value._embedded.app_external_aws_rds_connections.reduce<
        Record<string, DeployAppExternalAwsRdsConnection>
      >((acc, connection) => {
        const deserialized = deserializeAppExternalAwsRdsConnection(connection);
        acc[deserialized.id] = deserialized;
        return acc;
      }, {});

    yield* schema.update(
      schema.appExternalAwsRdsConnections.add(connectionsMap),
    );
  },
);

export const fetchAppExternalAwsRdsConnectionsByExternalAwsAccountId = api.get<
  { accountId: string },
  HalEmbedded<{
    app_external_aws_rds_connections: DeployAppExternalAwsRdsConnectionResponse[];
  }>
>(
  "/external_aws_accounts/:accountId/app_external_aws_rds_connections",
  function* (ctx, next) {
    yield* next();
    if (!ctx.json.ok) {
      return;
    }

    const { value } = ctx.json;
    const connectionsMap =
      value._embedded.app_external_aws_rds_connections.reduce<
        Record<string, DeployAppExternalAwsRdsConnection>
      >((acc, connection) => {
        const deserialized = deserializeAppExternalAwsRdsConnection(connection);
        acc[deserialized.id] = deserialized;
        return acc;
      }, {});

    yield* schema.update(
      schema.appExternalAwsRdsConnections.add(connectionsMap),
    );
  },
);

// Selectors
export const selectAppExternalAwsRdsConnectionsAsList =
  schema.appExternalAwsRdsConnections.selectTableAsList;

export const selectAppExternalAwsRdsConnectionById =
  schema.appExternalAwsRdsConnections.selectById;

export const selectAppExternalAwsRdsConnectionsByAppId = createSelector(
  selectAppExternalAwsRdsConnectionsAsList,
  (_: WebState, props: { appId: string }) => props.appId,
  (connections, appId) => connections.filter((conn) => conn.appId === appId),
);

export const selectAppExternalAwsRdsConnectionsByExternalAwsResourceId =
  createSelector(
    selectAppExternalAwsRdsConnectionsAsList,
    (_: WebState, props: { resourceId: string }) => props.resourceId,
    (connections, resourceId) =>
      connections.filter((conn) => conn.externalAwsResourceId === resourceId),
  );

export const selectAppExternalAwsRdsConnectionsByExternalAwsAccountId =
  createSelector(
    selectAppExternalAwsRdsConnectionsAsList,
    (_: WebState, props: { accountId: string }) => props.accountId,
    (connections, accountId) =>
      connections.filter((conn) => conn.externalAwsAccountId === accountId),
  );

export const selectExternalAwsResourcesViaAppExternalAwsRdsConnectionsByAppId =
  createSelector(
    (state: WebState, props: { appId: string }) =>
      selectAppExternalAwsRdsConnectionsByAppId(state, props),
    schema.externalAwsResources.selectTableAsList,
    (connections, resources) => {
      const resourceIds = connections
        .filter((conn) => conn.externalAwsResourceId !== "")
        .map((conn) => conn.externalAwsResourceId);

      return resources.filter((resource) => resourceIds.includes(resource.id));
    },
  );

export const selectAppsViaAppExternalAwsRdsConnectionsByExternalAwsResourceId =
  createSelector(
    (state: WebState, props: { resourceId: string }) =>
      selectAppExternalAwsRdsConnectionsByExternalAwsResourceId(state, props),
    schema.apps.selectTableAsList,
    (connections, apps) => {
      const appIds = connections
        .filter((conn) => conn.appId !== "")
        .map((conn) => conn.appId);

      return apps.filter((app) => appIds.includes(app.id));
    },
  );

// Entity registration for HAL parser
export const appExternalAwsRdsConnectionEntities = {
  app_external_aws_rds_connection: defaultEntity({
    id: "app_external_aws_rds_connection",
    deserialize: deserializeAppExternalAwsRdsConnection,
    save: schema.appExternalAwsRdsConnections.add,
  }),
};
